/**
 * Current DFP destinations. New navigation should use only these paths.
 * Old bookmarks live in DFP_LEGACY_ROUTES and are accepted only as redirects.
 */

function pathSegment(value: string): string {
  return encodeURIComponent(value);
}

function productMasterDetail(productCode: string): string {
  return `/dfp/product_master/${pathSegment(productCode)}`;
}

function standardDetail(standardCode: string): string {
  return `/dfp/standard_compliance/standard/${pathSegment(standardCode)}`;
}

function standardProduct(productCode: string): string {
  return `/dfp/standard_compliance/${pathSegment(productCode)}`;
}

function mintProductResults(productCode: string): string {
  return `/dfp/infra/mint/${pathSegment(productCode)}/results`;
}

/** Fill `:params` in a route pattern. Splats are left for the router. */
export function applyRouteParams(
  pattern: string,
  params: Readonly<Record<string, string | undefined>>,
): string {
  return pattern.replaceAll(/:(\w+)/g, (_match: string, key: string) =>
    pathSegment(params[key] ?? ''),
  );
}

/** Route pattern under the `/dfp` parent, for React Router `path` props. */
export function dfpRelativePath(absolutePath: string): string {
  return absolutePath.replace(/^\/dfp\/?/, '');
}

export const DFP_ROUTES = {
  ROOT: '/dfp',
  OVERVIEW: '/dfp',

  PRODUCT_MASTER: '/dfp/product_master',
  PRODUCT_MASTER_DETAIL: productMasterDetail,

  STANDARDS_CATALOG: '/dfp/standard_compliance',
  STANDARDS_ADOPTION: '/dfp/standard_compliance/standards',
  STANDARDS_PRODUCTS: '/dfp/standard_compliance/products',
  STANDARDS_TREND: '/dfp/standard_compliance/trend',
  STANDARD_DETAIL: standardDetail,
  STANDARD_PRODUCT: standardProduct,

  /** Same pages as the STANDARDS_* names. Kept so existing screens keep compiling. */
  STANDARD_COMPLIANCE: '/dfp/standard_compliance',
  STANDARD_COMPLIANCE_STANDARDS: '/dfp/standard_compliance/standards',
  STANDARD_COMPLIANCE_PRODUCTS: '/dfp/standard_compliance/products',
  STANDARD_COMPLIANCE_TREND: '/dfp/standard_compliance/trend',
  STANDARD_COMPLIANCE_PRODUCT: standardProduct,
  STANDARD_DETAILS: standardDetail,

  PLATFORMS: '/dfp/common_platform',
  PLATFORM_DASHBOARD: '/dfp/common_platform/dashboard',
  /** Same pages as PLATFORMS and PLATFORM_DASHBOARD. */
  COMMON_PLATFORM: '/dfp/common_platform',
  COMMON_PLATFORM_DASHBOARD: '/dfp/common_platform/dashboard',

  HEALTH_HUB: '/dfp/health_hub',
  TECHNICAL_DEBT: '/dfp/health_hub/technical_debt',
  HEALTH_HUB_TECHNICAL_DEBT: '/dfp/health_hub/technical_debt',
  HEALTH_HUB_TECHNICAL_DEBT_SOLUTIONS: '/dfp/health_hub/technical_debt/solutions',
  HEALTH_HUB_TECHNICAL_DEBT_TRENDS: '/dfp/health_hub/technical_debt/trends',
  HEALTH_HUB_TECHNICAL_DEBT_RECOMMENDATIONS: '/dfp/health_hub/technical_debt/recommendations',
  HEALTH_HUB_TECHNICAL_DEBT_SECURITY_SCAN_ISSUES: '/dfp/health_hub/technical_debt/security_scan_issues',
  HEALTH_HUB_TECHNICAL_DEBT_SECURITY_SCAN_ISSUE_DETAIL: (id: string) =>
    `/dfp/health_hub/technical_debt/security_scan_issues/${encodeURIComponent(id)}`,
  ARCHITECTURE_HEALTH: '/dfp/health_hub/arch_healthcheck',
  HEALTH_HUB_ARCH_HEALTHCHECK: '/dfp/health_hub/arch_healthcheck',
  HEALTH_HUB_ARCH_HEALTHCHECK_PRODUCT: (productIdOrCode: string) =>
    `/dfp/health_hub/arch_healthcheck/products/${encodeURIComponent(productIdOrCode)}`,
  HEALTH_HUB_TECHNICAL_STACK: '/dfp/health_hub/technical_stack',
  TECHNOLOGY_STACK: '/dfp/health_hub/technical_stack/overview',
  HEALTH_HUB_TECHNICAL_STACK_OVERVIEW: '/dfp/health_hub/technical_stack/overview',
  HEALTH_HUB_TECHNICAL_STACK_BACKEND_GO: '/dfp/health_hub/technical_stack/backend/go',
  HEALTH_HUB_TECHNICAL_STACK_BACKEND_KOTLIN: '/dfp/health_hub/technical_stack/backend/kotlin',
  HEALTH_HUB_TECHNICAL_STACK_FRONTEND_TYPESCRIPT: '/dfp/health_hub/technical_stack/frontend/typescript',

  INFRA: '/dfp/infra',
  MINT_DASHBOARD: '/dfp/infra/mint_dashboard',
  /** Same page as MINT_DASHBOARD. */
  INFRA_DASHBOARD: '/dfp/infra/mint_dashboard',
  MINT_PRODUCT_RESULTS: mintProductResults,

  CENTRAL_DASHBOARDS: '/dfp/central_dashboards',
} as const;

/**
 * Old bookmark patterns. The router mounts these and nowhere else.
 * Functions build an example URL from the same pattern.
 */
const LEGACY_PATTERNS = {
  PRODUCT_MASTER_HYPHEN: '/dfp/product-master',
  PRODUCT_MASTER_HYPHEN_DETAIL: '/dfp/product-master/:productCode',
  PRODUCT_INSIGHTS: '/dfp/product_insights',
  PRODUCT_INSIGHTS_DFP_API: '/dfp/product_insights/dfp-api',
  PRODUCT_INSIGHTS_PRODUCT_MASTER: '/dfp/product_insights/product-master',
  PRODUCT_INSIGHTS_DETAIL: '/dfp/product_insights/:productCode',
  PRODUCT_INSIGHTS_PRODUCT_MASTER_DETAIL: '/dfp/product_insights/product-master/:productCode',
  MATURITY_LEVEL: '/dfp/maturity_level',
  MATURITY_LEVEL_DASHBOARD: '/dfp/maturity_level/dashboard',
  MINT_PRODUCT_RESULTS: '/dfp/maturity_level/mint/:productCode/results',
  STANDARDS_COMPLIANCE: '/dfp/standard_compliance/compliance',
  SOFTWARE_QUALITY_ARCH: '/dfp/software_quality/arch_healthcheck',
  SOFTWARE_QUALITY_ARCH_PRODUCT: '/dfp/software_quality/arch_healthcheck/products/:id',
  SOFTWARE_QUALITY_TECH_STACK: '/dfp/software_quality/technical_stack',
  HEALTH_HUB_CQO: '/dfp/health_hub/cqo',
  SOFTWARE_QUALITY_CQO: '/dfp/software_quality/cqo',
  CENTRAL_DASHBOARDS_PRODUCTIVITY: '/dfp/central_dashboards/productivity',
  CENTRAL_DASHBOARDS_AI_MATURITY: '/dfp/central_dashboards/ai_maturity',
  CENTRAL_DASHBOARDS_QUALITY: '/dfp/central_dashboards/quality',
} as const;

export type DfpLegacyRedirect = {
  pattern: string;
  to: string | ((params: Readonly<Record<string, string | undefined>>) => string);
};

/** Old bookmarks. Redirect only. Do not navigate here from new screens. */
export const DFP_LEGACY_ROUTES = {
  PRODUCT_MASTER_HYPHEN: LEGACY_PATTERNS.PRODUCT_MASTER_HYPHEN,
  PRODUCT_MASTER_HYPHEN_DETAIL: (productCode: string) =>
    applyRouteParams(LEGACY_PATTERNS.PRODUCT_MASTER_HYPHEN_DETAIL, { productCode }),
  PRODUCT_INSIGHTS: LEGACY_PATTERNS.PRODUCT_INSIGHTS,
  PRODUCT_INSIGHTS_DFP_API: LEGACY_PATTERNS.PRODUCT_INSIGHTS_DFP_API,
  PRODUCT_INSIGHTS_PRODUCT_MASTER: LEGACY_PATTERNS.PRODUCT_INSIGHTS_PRODUCT_MASTER,
  PRODUCT_INSIGHTS_DETAIL: (productCode: string) =>
    applyRouteParams(LEGACY_PATTERNS.PRODUCT_INSIGHTS_DETAIL, { productCode }),
  PRODUCT_INSIGHTS_PRODUCT_MASTER_DETAIL: (productCode: string) =>
    applyRouteParams(LEGACY_PATTERNS.PRODUCT_INSIGHTS_PRODUCT_MASTER_DETAIL, { productCode }),
  MATURITY_LEVEL: LEGACY_PATTERNS.MATURITY_LEVEL,
  MATURITY_LEVEL_DASHBOARD: LEGACY_PATTERNS.MATURITY_LEVEL_DASHBOARD,
  MINT_PRODUCT_RESULTS: (productCode: string) =>
    applyRouteParams(LEGACY_PATTERNS.MINT_PRODUCT_RESULTS, { productCode }),
  STANDARDS_COMPLIANCE: LEGACY_PATTERNS.STANDARDS_COMPLIANCE,
  SOFTWARE_QUALITY_ARCH: LEGACY_PATTERNS.SOFTWARE_QUALITY_ARCH,
  SOFTWARE_QUALITY_TECH_STACK: LEGACY_PATTERNS.SOFTWARE_QUALITY_TECH_STACK,
  HEALTH_HUB_CQO: LEGACY_PATTERNS.HEALTH_HUB_CQO,
  SOFTWARE_QUALITY_CQO: LEGACY_PATTERNS.SOFTWARE_QUALITY_CQO,
  CENTRAL_DASHBOARDS_PRODUCTIVITY: LEGACY_PATTERNS.CENTRAL_DASHBOARDS_PRODUCTIVITY,
  CENTRAL_DASHBOARDS_AI_MATURITY: LEGACY_PATTERNS.CENTRAL_DASHBOARDS_AI_MATURITY,
  CENTRAL_DASHBOARDS_QUALITY: LEGACY_PATTERNS.CENTRAL_DASHBOARDS_QUALITY,
} as const;

/** Every legacy pattern the page router must mount, in specificity-friendly order. */
export const DFP_LEGACY_REDIRECTS: readonly DfpLegacyRedirect[] = [
  {
    pattern: LEGACY_PATTERNS.PRODUCT_MASTER_HYPHEN_DETAIL,
    to: params => DFP_ROUTES.PRODUCT_MASTER_DETAIL(params.productCode ?? ''),
  },
  {
    pattern: LEGACY_PATTERNS.PRODUCT_MASTER_HYPHEN,
    to: DFP_ROUTES.PRODUCT_MASTER,
  },
  {
    pattern: LEGACY_PATTERNS.PRODUCT_INSIGHTS_DFP_API,
    to: DFP_ROUTES.PRODUCT_MASTER,
  },
  {
    pattern: LEGACY_PATTERNS.PRODUCT_INSIGHTS_PRODUCT_MASTER,
    to: DFP_ROUTES.PRODUCT_MASTER,
  },
  {
    pattern: LEGACY_PATTERNS.PRODUCT_INSIGHTS_PRODUCT_MASTER_DETAIL,
    to: params => DFP_ROUTES.PRODUCT_MASTER_DETAIL(params.productCode ?? ''),
  },
  {
    pattern: LEGACY_PATTERNS.PRODUCT_INSIGHTS_DETAIL,
    to: params => DFP_ROUTES.PRODUCT_MASTER_DETAIL(params.productCode ?? ''),
  },
  {
    pattern: `${LEGACY_PATTERNS.PRODUCT_INSIGHTS}/*`,
    to: DFP_ROUTES.PRODUCT_MASTER,
  },
  {
    pattern: LEGACY_PATTERNS.MINT_PRODUCT_RESULTS,
    to: params => DFP_ROUTES.MINT_PRODUCT_RESULTS(params.productCode ?? ''),
  },
  {
    pattern: LEGACY_PATTERNS.MATURITY_LEVEL_DASHBOARD,
    to: DFP_ROUTES.MINT_DASHBOARD,
  },
  {
    pattern: LEGACY_PATTERNS.MATURITY_LEVEL,
    to: DFP_ROUTES.MINT_DASHBOARD,
  },
  {
    pattern: LEGACY_PATTERNS.STANDARDS_COMPLIANCE,
    to: DFP_ROUTES.STANDARDS_ADOPTION,
  },
  {
    pattern: LEGACY_PATTERNS.SOFTWARE_QUALITY_ARCH_PRODUCT,
    to: params => DFP_ROUTES.HEALTH_HUB_ARCH_HEALTHCHECK_PRODUCT(params.id ?? ''),
  },
  {
    pattern: LEGACY_PATTERNS.SOFTWARE_QUALITY_ARCH,
    to: DFP_ROUTES.ARCHITECTURE_HEALTH,
  },
  {
    pattern: `${LEGACY_PATTERNS.SOFTWARE_QUALITY_TECH_STACK}/*`,
    to: DFP_ROUTES.TECHNOLOGY_STACK,
  },
  {
    pattern: LEGACY_PATTERNS.HEALTH_HUB_CQO,
    to: DFP_ROUTES.TECHNICAL_DEBT,
  },
  {
    pattern: LEGACY_PATTERNS.SOFTWARE_QUALITY_CQO,
    to: DFP_ROUTES.TECHNICAL_DEBT,
  },
  {
    pattern: LEGACY_PATTERNS.CENTRAL_DASHBOARDS_PRODUCTIVITY,
    to: DFP_ROUTES.CENTRAL_DASHBOARDS,
  },
  {
    pattern: LEGACY_PATTERNS.CENTRAL_DASHBOARDS_AI_MATURITY,
    to: DFP_ROUTES.CENTRAL_DASHBOARDS,
  },
  {
    pattern: LEGACY_PATTERNS.CENTRAL_DASHBOARDS_QUALITY,
    to: DFP_ROUTES.CENTRAL_DASHBOARDS,
  },
  {
    pattern: `${DFP_ROUTES.CENTRAL_DASHBOARDS}/*`,
    to: DFP_ROUTES.CENTRAL_DASHBOARDS,
  },
];

export type DfpNavIconKey =
  | 'overview'
  | 'productMaster'
  | 'standards'
  | 'platforms'
  | 'healthHub'
  | 'infra'
  | 'dashboards';

export interface DfpNavItem {
  id: string;
  label: string;
  route?: string;
  iconKey?: DfpNavIconKey;
  /** Highlight this item for any path under its route. */
  usePrefix?: boolean;
  children?: DfpNavItem[];
}

/** Sidebar structure. Icons are applied in the app, not in this package. */
export const DFP_NAVIGATION: readonly DfpNavItem[] = [
  { id: 'overview', label: 'Overview', route: DFP_ROUTES.OVERVIEW, iconKey: 'overview' },
  {
    id: 'product-master',
    label: 'Product Master',
    route: DFP_ROUTES.PRODUCT_MASTER,
    iconKey: 'productMaster',
  },
  {
    id: 'mfts',
    label: 'MF Technical Standards',
    iconKey: 'standards',
    children: [
      { id: 'mfts-catalog', label: 'Standards Catalog', route: DFP_ROUTES.STANDARDS_CATALOG },
      {
        id: 'mfts-compliance',
        label: 'Compliance Dashboard',
        children: [
          { id: 'mfts-adoption', label: 'Standards Adoption', route: DFP_ROUTES.STANDARDS_ADOPTION },
          { id: 'mfts-product', label: 'Product Compliance', route: DFP_ROUTES.STANDARDS_PRODUCTS },
          { id: 'mfts-trend', label: 'Trend', route: DFP_ROUTES.STANDARDS_TREND },
        ],
      },
    ],
  },
  {
    id: 'platforms',
    label: 'Platforms',
    iconKey: 'platforms',
    children: [
      { id: 'platforms-mfbs', label: 'MFBS Portal', route: DFP_ROUTES.PLATFORMS },
      { id: 'platforms-dashboard', label: 'Platform Dashboard', route: DFP_ROUTES.PLATFORM_DASHBOARD },
    ],
  },
  {
    id: 'engineering-health-hub',
    label: 'Engineering Health Hub',
    iconKey: 'healthHub',
    children: [
      { id: 'ehh-technical-debt', label: 'Technical Debt', route: DFP_ROUTES.TECHNICAL_DEBT, usePrefix: true },
      { id: 'ehh-architecture', label: 'Architecture', route: DFP_ROUTES.ARCHITECTURE_HEALTH, usePrefix: true },
      { id: 'ehh-tech-stack', label: 'Technology Stack', route: DFP_ROUTES.HEALTH_HUB_TECHNICAL_STACK, usePrefix: true },
    ],
  },
  { id: 'infra', label: 'Infra', route: DFP_ROUTES.INFRA, iconKey: 'infra', usePrefix: true },
  {
    id: 'central-dashboards',
    label: 'Central Dashboards',
    route: DFP_ROUTES.CENTRAL_DASHBOARDS,
    iconKey: 'dashboards',
  },
];

/** Overview card key → current page. Cards without an entry have no internal page. */
export const DFP_OVERVIEW_ROUTES: Record<string, string> = {
  product_master: DFP_ROUTES.PRODUCT_MASTER,
  standards_catalog: DFP_ROUTES.STANDARDS_CATALOG,
  standards_adoption: DFP_ROUTES.STANDARDS_ADOPTION,
  platforms: DFP_ROUTES.PLATFORMS,
  platform_dashboard: DFP_ROUTES.PLATFORM_DASHBOARD,
  mint_dashboard: DFP_ROUTES.MINT_DASHBOARD,
  microservice_metadata: DFP_ROUTES.PRODUCT_MASTER,
  technology_stack: DFP_ROUTES.TECHNOLOGY_STACK,
  service_dependencies: DFP_ROUTES.PRODUCT_MASTER,
  maturity_level: DFP_ROUTES.MINT_DASHBOARD,
  standard_compliance: DFP_ROUTES.STANDARDS_CATALOG,
  common_platforms: DFP_ROUTES.PLATFORMS,
  technical_debt: DFP_ROUTES.TECHNICAL_DEBT,
  architecture_health: DFP_ROUTES.ARCHITECTURE_HEALTH,
  security_scan_issues: DFP_ROUTES.HEALTH_HUB_TECHNICAL_DEBT_SECURITY_SCAN_ISSUES,
  central_dashboards: DFP_ROUTES.CENTRAL_DASHBOARDS,
};

/** Repo detail under Technology Stack (no `dashboard` segment). */
export function healthHubTechnicalStackRepo(
  repoType: string,
  repository: string,
): string {
  return `${DFP_ROUTES.HEALTH_HUB_TECHNICAL_STACK}/repositories/${repoType}/${encodeURIComponent(repository)}`;
}

export function buildRouteWithParams(
  route: string,
  params: Record<string, string | number | boolean>
): string {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    searchParams.set(key, String(value));
  });
  const queryString = searchParams.toString();
  return queryString ? `${route}?${queryString}` : route;
}

export function extractProductMasterCode(pathname: string): string | null {
  const match = /\/dfp\/product_master\/([^/]+)/.exec(pathname);
  return match?.[1] ?? null;
}

export function extractStandardCode(pathname: string): string | null {
  const match = /\/dfp\/standard_compliance\/standard\/([^/]+)/.exec(pathname);
  return match?.[1] ?? null;
}

export function extractMintProductCode(pathname: string): string | null {
  const match = /\/dfp\/infra\/mint\/([^/]+)\/results/.exec(pathname);
  return match?.[1] ?? null;
}
